import * as Sentry from "@sentry/react";
import { Switch, Route, useLocation, useRoute } from "wouter";
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ConfirmDialogProvider } from "@/components/ui/confirm-dialog";
import { useGetMe, useGetCompanyBySlug, useSelectCompany, getGetMeQueryKey, getListCompaniesQueryKey } from "@workspace/api-client-react";
import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { trackPageView } from "@/lib/metrics";
import { DemoProfileProvider } from "@/context/DemoProfileContext";

import Home from "./pages/website/Home";
import PrivacyPolicy from "./pages/website/PrivacyPolicy";
import TermsOfService from "./pages/website/TermsOfService";
import RefundPolicy from "./pages/website/RefundPolicy";
import Login from "./pages/app/Login";
import CpLogin from "./pages/cp/CpLogin";
import Signup from "./pages/app/Signup";
import AcceptInvite from "./pages/app/AcceptInvite";
import DemoConfirm from "./pages/app/DemoConfirm";
import SetupWizard from "./pages/app/SetupWizard";
import ForgotPassword from "./pages/app/ForgotPassword";
import ResetPassword from "./pages/app/ResetPassword";
import CompanyPortal from "./pages/CompanyPortal";
import IntegrationsPage from "./pages/IntegrationsPage";
import ProjectApp from "./pages/ProjectApp";
import ControlPanel from "./pages/cp/ControlPanel";
import NotFound from "./pages/not-found";
import ChatWidget from "./components/ChatWidget";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: 30_000,
    },
  },
  queryCache: new QueryCache({
    onError: (error: unknown) => {
      if (error && typeof error === "object" && "status" in error) {
        const status = (error as { status: number }).status;
        if (status === 401 || status === 403) return;
      }
      Sentry.captureException(error, { tags: { source: "react-query-query" } });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error: unknown) => {
      if (error && typeof error === "object" && "status" in error) {
        const status = (error as { status: number }).status;
        if (status === 401 || status === 403) return;
      }
      Sentry.captureException(error, { tags: { source: "react-query-mutation" } });
    },
  }),
});

function Redirect({ to }: { to: string }) {
  const [, setLocation] = useLocation();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    setLocation(to);
  }, [to]);
  return null;
}

function LoadingScreen() {
  return (
    <div className="min-h-screen w-full flex items-center justify-center" style={{ background: "linear-gradient(135deg, #0d4a4a 0%, #1a3a5c 60%, #1a2a5a 100%)" }}>
      <div className="flex flex-col items-center gap-5">
        <img src="/images/finproject-logo.png" alt="FinProject" className="w-16 h-16 object-contain animate-pulse" />
        <p className="text-white/60 text-sm font-medium animate-pulse">Loading FinProject...</p>
      </div>
    </div>
  );
}

function AccessDenied() {
  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-background">
      <div className="text-center max-w-sm">
        <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-5">
          <svg className="w-8 h-8 text-muted-foreground" fill="none" viewBox="0 0 24 24">
            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
        </div>
        <h2 className="text-xl font-bold mb-2">Company Not Found</h2>
        <p className="text-muted-foreground text-sm mb-6">
          This company doesn't exist or you don't have access to it.
        </p>
        <a href="/cp" className="text-primary hover:underline text-sm">Return to control panel</a>
      </div>
    </div>
  );
}

function SentryErrorFallback() {
  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-background">
      <div className="text-center max-w-sm">
        <h2 className="text-xl font-bold mb-2">Something went wrong</h2>
        <p className="text-muted-foreground text-sm mb-6">
          An unexpected error occurred. Our team has been notified.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="text-primary hover:underline text-sm"
        >
          Reload page
        </button>
      </div>
    </div>
  );
}

function needsSetup(user: { company?: { setupComplete?: boolean; isDemo?: boolean } | null; role?: string }) {
  return user.company?.setupComplete === false && !user.company?.isDemo && (user.role === "Admin" || user.role === "Super Admin");
}

function isSuperAdmin(user: { role?: string }) {
  return user.role === "Super Admin";
}

function userAppRedirect(user: { companyId?: string | null; company?: { slug?: string; setupComplete?: boolean; isDemo?: boolean } | null; role?: string }) {
  const slug = user.company?.slug;
  if (!slug) return isSuperAdmin(user) ? <Redirect to="/cp" /> : <Redirect to="/app/login" />;
  if (needsSetup(user)) return <Redirect to="/app/setup" />;
  return <Redirect to={`/app/${slug}`} />;
}

function HomeRoute() {
  const { data: user, isLoading } = useGetMe();
  if (isLoading) return <LoadingScreen />;
  if (user) {
    if (user.companyId) return userAppRedirect(user);
    if (isSuperAdmin(user)) return <Redirect to="/cp" />;
    return <Home />;
  }
  return <Home />;
}

function AppLoginRoute() {
  const { data: user, isLoading } = useGetMe();
  if (isLoading) return <LoadingScreen />;
  if (user) {
    if (user.companyId) return userAppRedirect(user);
    if (isSuperAdmin(user)) return <Redirect to="/cp" />;
    return <Login />;
  }
  return <Login />;
}

function CpLoginRoute() {
  const { data: user, isLoading } = useGetMe();
  if (isLoading) return <LoadingScreen />;
  // Only auto-redirect if the session is already a Super Admin with no company context.
  // Any other logged-in state (e.g. a live demo session) must NOT bleed through —
  // the CP login form handles clearing the old session itself.
  if (user && isSuperAdmin(user) && !user.companyId) return <Redirect to="/cp" />;
  return <CpLogin />;
}

function ProtectedCP() {
  const { data: user, isLoading, error } = useGetMe();
  if (isLoading) return <LoadingScreen />;
  if (error || !user) return <Redirect to="/cp/login" />;
  // Non-super-admins must never reach the CP, even if they have an active session.
  if (!isSuperAdmin(user)) return <Redirect to="/app/login" />;
  // Super Admin is always allowed into the CP regardless of companyId on the session
  // (companyId may be set when the admin impersonates a company — that is handled inside ControlPanel).
  return <ControlPanel />;
}

function ProtectedApp() {
  const [, params] = useRoute("/app/:slug");
  const slug = params?.slug || "";
  const { data: user, isLoading: userLoading, error: userError } = useGetMe();
  const { data: companyInfo, isLoading: slugLoading, error: slugError } = useGetCompanyBySlug(slug, {
    query: { enabled: !!slug && !!user },
  });
  const selectCompany = useSelectCompany();
  const qc = useQueryClient();
  const [, setLocation] = useLocation();
  const [switching, setSwitching] = useState(false);
  const switchingRef = useRef(false);

  useEffect(() => {
    if (!companyInfo || !user) return;
    if (user.companyId !== companyInfo.id && !switchingRef.current) {
      switchingRef.current = true;
      setSwitching(true);
      selectCompany.mutateAsync({ data: { companyId: companyInfo.id } })
        .then(() => Promise.all([
          qc.invalidateQueries({ queryKey: getGetMeQueryKey() }),
          qc.invalidateQueries({ queryKey: getListCompaniesQueryKey() }),
        ]))
        .catch(() => setLocation("/cp"))
        .finally(() => {
          switchingRef.current = false;
          setSwitching(false);
        });
    }
  }, [user?.companyId, companyInfo?.id]);

  if (userLoading || slugLoading || switching) return <LoadingScreen />;
  if (!user) return <Redirect to="/app/login" />;
  if (!user.companyId && !slug) return <Redirect to="/cp" />;
  if (slugError || !companyInfo) return <AccessDenied />;
  if (needsSetup(user)) return <Redirect to="/app/setup" />;

  return <CompanyPortal />;
}

function ProtectedProjectApp() {
  const [, params] = useRoute("/app/:slug/project/:id");
  const slug = params?.slug || "";
  const { data: user, isLoading, error } = useGetMe();
  const { data: companyInfo, isLoading: slugLoading, error: slugError } = useGetCompanyBySlug(slug, {
    query: { enabled: !!slug && !!user },
  });
  const selectCompany = useSelectCompany();
  const qc = useQueryClient();
  const [, setLocation] = useLocation();
  const [switching, setSwitching] = useState(false);
  const switchingRef = useRef(false);

  useEffect(() => {
    if (!companyInfo || !user) return;
    if (user.companyId !== companyInfo.id && !switchingRef.current) {
      switchingRef.current = true;
      setSwitching(true);
      selectCompany.mutateAsync({ data: { companyId: companyInfo.id } })
        .then(() => Promise.all([
          qc.invalidateQueries({ queryKey: getGetMeQueryKey() }),
          qc.invalidateQueries({ queryKey: getListCompaniesQueryKey() }),
        ]))
        .catch(() => setLocation("/cp"))
        .finally(() => {
          switchingRef.current = false;
          setSwitching(false);
        });
    }
  }, [user?.companyId, companyInfo?.id]);

  if (isLoading || slugLoading || switching) return <LoadingScreen />;
  if (!user) return <Redirect to="/app/login" />;
  if (slugError || !companyInfo) return <AccessDenied />;
  if (needsSetup(user)) return <Redirect to="/app/setup" />;
  return <ProjectApp />;
}

function ProtectedSetup() {
  const { data: user, isLoading, error } = useGetMe();
  if (isLoading) return <LoadingScreen />;
  if (error || !user) return <Redirect to="/app/login" />;
  if (!user.companyId) return <Redirect to="/cp" />;
  return <SetupWizard />;
}

function AppRouter() {
  return (
    <Switch>
      <Route path="/" component={HomeRoute} />
      <Route path="/privacy" component={PrivacyPolicy} />
      <Route path="/terms" component={TermsOfService} />
      <Route path="/refund-policy" component={RefundPolicy} />
      <Route path="/pricing">{() => { window.location.replace("/#pricing"); return null; }}</Route>
      <Route path="/app/login" component={AppLoginRoute} />
      <Route path="/cp/login" component={CpLoginRoute} />
      <Route path="/login"><Redirect to="/app/login" /></Route>
      <Route path="/signup" component={Signup} />
      <Route path="/forgot-password" component={ForgotPassword} />
      <Route path="/reset-password/:token" component={ResetPassword} />
      <Route path="/accept-invite/:token" component={AcceptInvite} />
      <Route path="/demo/confirm/:token" component={DemoConfirm} />
      <Route path="/app/setup" component={ProtectedSetup} />
      <Route path="/cp" component={ProtectedCP} />
      <Route path="/cp/:rest*" component={ProtectedCP} />
      <Route path="/app/:slug/integrations" component={IntegrationsPage} />
      <Route path="/app/:slug/project/:id" component={ProtectedProjectApp} />
      <Route path="/app/:slug" component={ProtectedApp} />
      <Route path="/app"><Redirect to="/cp" /></Route>
      <Route path="/portal"><Redirect to="/cp" /></Route>
      <Route component={NotFound} />
    </Switch>
  );
}

function AppInner() {
  const { data: user } = useGetMe();
  const [location] = useLocation();

  useEffect(() => {
    trackPageView(location);
  }, [location]);

  useEffect(() => {
    if (user) {
      Sentry.setUser({
        id: user.id,
        email: user.email,
        username: user.name,
      });
      Sentry.setTag("company_slug", user.company?.slug ?? "");
    } else {
      Sentry.setUser(null);
      Sentry.setTag("company_slug", "");
    }
  }, [user]);

  const showChat = location === "/" || location.startsWith("/app/");

  return (
    <TooltipProvider>
      <ConfirmDialogProvider>
        <AppRouter />
        {showChat && <ChatWidget />}
        <Toaster />
      </ConfirmDialogProvider>
    </TooltipProvider>
  );
}

export default function App() {
  return (
    <Sentry.ErrorBoundary fallback={<SentryErrorFallback />}>
      <QueryClientProvider client={queryClient}>
        <DemoProfileProvider>
          <AppInner />
        </DemoProfileProvider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
}
